import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Tooltip that renders via portal to escape overflow clipping.
 * Uses position: fixed so it's always visible regardless of parent overflow.
 */
const AddTagTooltip = ({ children }) => {
  const [tooltipState, setTooltipState] = useState({ visible: false, top: 0, left: 0 });
  const triggerRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const cancelHide = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const getPosition = () => {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = 320;
    const padding = 8;
    const viewportWidth = window.innerWidth;

    // Position below the icon, right-aligned so it extends left
    let left = rect.left + rect.width - tooltipWidth;
    const top = rect.bottom + padding;

    // Keep within viewport
    if (left < padding) left = padding;
    if (left + tooltipWidth > viewportWidth - padding) {
      left = viewportWidth - tooltipWidth - padding;
    }

    return { top, left };
  };

  const handleMouseEnter = () => {
    cancelHide();
    const pos = getPosition();
    if (pos) {
      setTooltipState({ visible: true, ...pos });
    }
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setTooltipState((s) => ({ ...s, visible: false }));
    }, 100);
  };

  useEffect(() => {
    if (tooltipState.visible && triggerRef.current) {
      const handleScroll = () => {
        const pos = getPosition();
        if (pos) setTooltipState((s) => ({ ...s, ...pos }));
      };
      window.addEventListener('scroll', handleScroll, true);
      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  }, [tooltipState.visible]);

  const tooltipContent = tooltipState.visible ? (
    <div
      className="add-tag-tooltip-portal"
      style={{
        position: 'fixed',
        top: tooltipState.top,
        left: tooltipState.left,
        zIndex: 999999,
        maxWidth: 320,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="tooltip"
    >
      {children}
    </div>
  ) : null;

  return (
    <span
      className="tooltip-container"
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="tooltip-icon" />
      {createPortal(tooltipContent, document.body)}
    </span>
  );
};

export default AddTagTooltip;
